import { useContext, useState, useEffect } from "react";
import ButtonSolid from "../Components/ButtonSolid";
import ButtonOutline from "../Components/ButtonOutline";
import { baseURL } from "../config";
import { useNavigate } from "react-router";
import axios from "axios";
import AuthContext from "../Context/AuthContext";
import toast from "react-hot-toast";
import DeletePopUp from "../Components/DeletePopUp";
import OverlayLoading from "../Components/OverlayLoading";
import MODsAssemblyList from "../Components/MODsAssemblyList";
import TemplateHeaders from "../Components/TemplateHeaders";
import { DeleteIcon } from "../Components/Icons";
import PopupWithClose from "../Components/PopupWithClose";
import AssetList from "../Components/AssetList";
import LoadingAnimation from "../Components/LoadingAnimation";
import InputField from "../Components/InputField";
import TextField from "../Components/TextField";
import { useParams } from "react-router";
import { useUnsavedChanges } from "../hooks/useUnsavedChanges";

const EMPTY_FIELDS = { name: "", model_description: "" };

const flattenIds = (groups) =>
  Object.values(groups).flatMap((items) => items.map((item) => item._id));
const idsKey = (ids) => [...ids].sort().join(",");

// Used for both adding (no :assembly_id in the route) and editing a MODs template.
// A template has one or more required main assemblies plus optional sub assemblies.
function ModsTemplateEdit() {
  const { assembly_id } = useParams();
  const isEdit = Boolean(assembly_id);
  const [toggleSearchPopup, setToggleSearchPopup] = useState(false);
  const [fetchingTemplateDetails, setFetchingTemplateDetails] = useState(isEdit);
  const [templateDetails, setTemplateDetails] = useState({}); // sub assemblies grouped by type
  const [originalTemplateDetails, setOriginalTemplateDetails] = useState({});
  const [mains, setMains] = useState([]); // main assemblies, in order
  const [originalMains, setOriginalMains] = useState([]);
  // While true the list on the right adds main assemblies; after "Done" it adds sub assemblies.
  const [mainMode, setMainMode] = useState(!isEdit);
  const { token, logout } = useContext(AuthContext);
  const [templateModelDesc, setTemplateModelDesc] = useState({});
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [originalFields, setOriginalFields] = useState(EMPTY_FIELDS);
  const [fieldErrors, setFieldErrors] = useState({});
  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [overlay, setOverlay] = useState(false);
  const [fetchingAssets, setFetchingAssets] = useState(true);
  const [filesPopup, setFilesPopup] = useState(false);
  const [assetsData, setAssetsData] = useState([]);
  const navigate = useNavigate();

  const mainIds = mains.map((main) => main._id);
  const fieldsChanged =
    fields.name.trim() !== originalFields.name ||
    fields.model_description.trim() !== originalFields.model_description;
  const assembliesChanged =
    mainIds.join(",") !== originalMains.map((main) => main._id).join(",") ||
    idsKey(flattenIds(templateDetails)) !==
      idsKey(flattenIds(originalTemplateDetails));
  const hasChanges = fieldsChanged || assembliesChanged;
  const { allowNavigation } = useUnsavedChanges(hasChanges);
  // Older templates have no saved main assemblies: their first assembly type group is
  // pre-selected as the mains until the template is saved.
  const legacyMains =
    isEdit && templateModelDesc.has_main_assemblies === false && mains.length > 0;

  const updateField = (key, value) => {
    setFields((prev) => ({ ...prev, [key]: value }));
    setFieldErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const resetChanges = () => {
    setTemplateDetails(originalTemplateDetails);
    setMains(originalMains);
    setMainMode(originalMains.length === 0);
    setFields(originalFields);
    setFieldErrors({});
  };

  const fetchAssets = async (param) => {
    try {
      setFetchingAssets(true);
      const response = await axios.get(`${baseURL}/asset/getAssets`, {
        headers: {
          "ngrok-skip-browser-warning": "any",
          "Content-Type": "multipart/form-data",
          "x-access-token": token,
        },
        params: {
          asset_ids: param,
        },
      });
      setAssetsData(response.data.data.assetData);
    } catch (error) {
      console.error("Error:", error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setFetchingAssets(false);
    }
  };

  useEffect(() => {
    if (!isEdit) return;
    // Only the latest request is applied (React StrictMode runs this effect twice in dev),
    // so a slower duplicate response can't overwrite edits made in the meantime.
    let ignore = false;
    const fetchData = async (param) => {
      try {
        setFetchingTemplateDetails(true);
        const response = await axios.get(
          `${baseURL}/template/getTemplateById/${param}`,
          {
            headers: {
              "ngrok-skip-browser-warning": "any",
              "Content-Type": "multipart/form-data",
              "x-access-token": token,
            },
            params: {
              assembly_id: assembly_id,
            },
          }
        );
        if (ignore) return;
        const templateDesc = response.data.data.templateDesc;
        const loadedFields = {
          name: templateDesc.name || "",
          model_description: templateDesc.model_description || "",
        };
        const loadedMains = response.data.data.mainAssemblies || [];
        const loadedSubs = response.data.data.templateData;
        setTemplateModelDesc(templateDesc);
        setFields(loadedFields);
        setOriginalFields(loadedFields);
        setMains(loadedMains);
        setOriginalMains(loadedMains);
        setMainMode(loadedMains.length === 0);
        setOriginalTemplateDetails(loadedSubs);
        setTemplateDetails(loadedSubs);
      } catch (error) {
        console.error("Error:", error);
      } finally {
        if (!ignore) setFetchingTemplateDetails(false);
      }
    };

    fetchData(assembly_id);
    return () => {
      ignore = true;
    };
  }, [assembly_id, isEdit, token]);

  const showDrawing = (assets) => {
    setFilesPopup(true);
    if (assets?.length > 0) {
      fetchAssets(assets);
    } else {
      setAssetsData([]);
      setFetchingAssets(false);
    }
  };

  const addMain = (dataSet) => {
    if (mainIds.includes(dataSet._id)) {
      toast.error("Already a main assembly");
      return;
    }
    // An assembly that was a sub assembly becomes a main instead of staying in both lists
    setTemplateDetails((prev) =>
      Object.fromEntries(
        Object.entries(prev).map(([type, items]) => [
          type,
          items.filter((item) => item._id !== dataSet._id),
        ])
      )
    );
    setMains((prev) => [...prev, dataSet]);
    setFieldErrors((prev) => ({ ...prev, main: "" }));
  };

  const removeMain = (id) => {
    setMains((prev) => prev.filter((main) => main._id !== id));
  };

  const finishMainSelection = () => {
    if (mains.length === 0) {
      setFieldErrors((prev) => ({ ...prev, main: "Add at least one main assembly" }));
      return;
    }
    setMainMode(false);
    setToggleSearchPopup(false);
  };

  const startMainSelection = () => {
    setMainMode(true);
    setToggleSearchPopup(true);
  };

  const addSubAssembly = (dataSet) => {
    if (mainIds.includes(dataSet._id)) {
      toast.error("This is already a main assembly");
      return;
    }
    const assemblyType = dataSet.assembly_type;
    if (templateDetails[assemblyType]?.some((item) => item._id === dataSet._id)) {
      toast.error("Assembly Already Exist");
      return;
    }
    setTemplateDetails((prevState) => ({
      ...prevState,
      [assemblyType]: [...(prevState[assemblyType] || []), dataSet],
    }));
    const element = document.getElementById(assemblyType);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  const deleteSubAssembly = (key, itemId) => {
    setTemplateDetails((prev) => ({
      ...prev,
      [key]: prev[key].filter((item) => item._id !== itemId),
    }));
  };

  const validateFields = () => {
    const errors = {};
    if (!fields.name.trim()) errors.name = "Model # is required";
    if (!fields.model_description.trim())
      errors.model_description = "Model description is required";
    if (mains.length === 0) errors.main = "Main assembly is required";
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSaveError = (error, fallbackMessage) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;
    if (status === 401) {
      logout();
    } else if (status === 409) {
      setFieldErrors((prev) => ({ ...prev, name: message }));
      toast.error(message);
    } else {
      toast.error(message || fallbackMessage);
    }
  };

  const templatePayload = () => ({
    name: fields.name.trim(),
    model_description: fields.model_description.trim(),
    main_assemblies: mainIds,
    sub_assemblies: flattenIds(templateDetails),
  });

  const createTemplate = async () => {
    try {
      setOverlay(true);
      const response = await axios.post(
        `${baseURL}/template/addTemplate`,
        templatePayload(),
        {
          headers: {
            "ngrok-skip-browser-warning": "any",
            "x-access-token": token,
          },
        }
      );
      if (response.status === 201) {
        toast.success("Template created");
        allowNavigation();
        navigate(`/mods_template/mods_template_detail/${response.data.data.id}`);
      }
    } catch (error) {
      handleSaveError(error, "Something Went Wrong, Template Not Created");
    } finally {
      setOverlay(false);
    }
  };

  const saveTemplateChanges = async (id) => {
    if (!hasChanges) {
      toast.error("No Changes Made");
      return;
    }
    try {
      setOverlay(true);
      const response = await axios.put(
        `${baseURL}/template/modifyTemplate/${id}`,
        templatePayload(),
        {
          headers: {
            "ngrok-skip-browser-warning": "any",
            "x-access-token": token,
          },
        }
      );
      if (response.status === 200) {
        toast.success("Template changes saved");
        allowNavigation();
        navigate(`/mods_template/mods_template_detail/${id}`);
      }
    } catch (error) {
      handleSaveError(error, "Something Went Wrong, Template Not Updated");
    } finally {
      setOverlay(false);
    }
  };

  const handleSave = () => {
    if (!validateFields()) return;
    if (isEdit) {
      saveTemplateChanges(templateModelDesc["id"]);
    } else {
      createTemplate();
    }
  };

  const deleteTemplate = async (id) => {
    try {
      setOverlay(true);
      const response = await axios.delete(
        `${baseURL}/template/deleteTemplate/${id}`,
        {
          headers: {
            "ngrok-skip-browser-warning": "any",
            "x-access-token": token,
          },
          params: {
            template_id: id,
          },
        }
      );
      if (response.status === 200) {
        toast.success("Template Deleted Successfully");
        allowNavigation();
        navigate("/mods_template");
      }
    } catch (error) {
      toast.error("Something Went Wrong Template Not Deleted");
    } finally {
      setOverlay(false);
    }
  };

  const hasSubAssemblies = Object.keys(templateDetails).some(
    (key) => templateDetails[key].length > 0
  );

  return (
    <>
      {overlay && <OverlayLoading />}
      <div className="flex w-full lg:h-[calc(100vh-80px)]">
        <div className="w-full lg:pr-3 overflow-y-auto">
          <div className="flex flex-wrap items-start md:items-center justify-between py-3 md:py-5 gap-2">
            <TemplateHeaders
              templateModelDesc={
                isEdit
                  ? templateModelDesc
                  : { name: "New Template" }
              }
              loading={fetchingTemplateDetails}
              modsTemplate={true}
              hideDescription={true}
            />
            <div className="mr-0 ml-auto">
              {isEdit && (
                <ButtonOutline
                  buttonText={"Delete This Template"}
                  disabled={fetchingTemplateDetails}
                  className="mr-4 mb-2"
                  onClick={() => setOpenDeletePopup(true)}
                />
              )}
              <ButtonOutline
                buttonText={"Reset Changes"}
                disabled={!hasChanges}
                className="mr-4 mb-2"
                onClick={() => resetChanges()}
              />
              <ButtonSolid
                buttonText={isEdit ? "Save Changes" : "Create Template"}
                disabled={fetchingTemplateDetails}
                onClick={handleSave}
              />
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg mb-5 w-full py-4 px-6">
            <p className="text-2xl font-semibold mb-4">Template Details</p>
            <div className="grid md:grid-cols-2 gap-x-6">
              <div>
                <InputField
                  labelId="template_model_number"
                  labelText={
                    <>
                      Model # <span className="text-red-600">*</span>
                    </>
                  }
                  placeholder="Enter model #"
                  value={fields.name}
                  disabled={fetchingTemplateDetails}
                  onChange={(e) => updateField("name", e.target.value)}
                  errorMessage={fieldErrors.name}
                />
              </div>
              <div className="md:col-span-2 mt-2">
                <TextField
                  labelId="template_model_description"
                  labelText={
                    <>
                      Model Description <span className="text-red-600">*</span>
                    </>
                  }
                  placeholder="Enter model description"
                  rows="3"
                  value={fields.model_description}
                  disabled={fetchingTemplateDetails}
                  onChange={(e) =>
                    updateField("model_description", e.target.value)
                  }
                  errorMessage={fieldErrors.model_description}
                />
              </div>
            </div>
          </div>
          <div className="bg-white shadow-md rounded-lg mb-5 w-full py-4 px-6">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <p className="text-2xl font-semibold">
                Main Assemblies <span className="text-red-600">*</span>
              </p>
              {mainMode ? (
                <ButtonSolid
                  buttonText={"Done – Select Sub Assemblies"}
                  disabled={fetchingTemplateDetails || mains.length === 0}
                  onClick={finishMainSelection}
                />
              ) : (
                <ButtonOutline
                  buttonText={"Edit Main Assemblies"}
                  disabled={fetchingTemplateDetails}
                  onClick={startMainSelection}
                />
              )}
            </div>
            {legacyMains && (
              <p className="text-sm text-gray-600 mb-3 rounded-md bg-orange-50 border border-orange-200 px-3 py-2">
                This template was created before main assemblies were saved
                separately. The assemblies of its first assembly type group are
                pre-selected as the main assemblies. Review them and save to
                update the template.
              </p>
            )}
            {fetchingTemplateDetails ? (
              <LoadingAnimation />
            ) : mains.length > 0 ? (
              <div className="flex flex-col gap-3">
                {mains.map((main) => (
                  <div
                    key={main._id}
                    data-main-assembly={main.assembly_number}
                    className={`border rounded-lg p-4 flex flex-wrap items-start justify-between gap-3 cursor-default ${
                      mainMode ? "border-orange-400" : "border-gray-300"
                    }`}
                  >
                    <div>
                      <p className="text-lg font-medium mb-1">
                        {`Assembly# - ${main.assembly_number}`}
                      </p>
                      <p className="text-gray-600 mb-2">
                        {main.assembly_description}
                      </p>
                      <div className="px-4 border border-gray-500 w-fit rounded-full text-gray-500">
                        {main.assembly_type}
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span
                        onClick={() => showDrawing(main.assets)}
                        className="cursor-pointer border-b border-gray-600 hover:border-orange-600"
                      >
                        Drawing
                      </span>
                      {mainMode && (
                        <div
                          title="Remove main assembly"
                          onClick={() => removeMain(main._id)}
                          className="cursor-pointer"
                        >
                          <DeleteIcon />
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div
                className={`border border-dashed rounded-lg p-4 flex flex-wrap items-center justify-between gap-3 ${
                  fieldErrors.main ? "border-red-600" : "border-gray-300"
                }`}
              >
                <p className="text-gray-500">
                  No main assembly selected. Choose one or more from the
                  assembly list.
                </p>
                <ButtonSolid
                  buttonText={"Select Main Assemblies"}
                  onClick={() => setToggleSearchPopup(true)}
                  className="lg:hidden"
                />
              </div>
            )}
            {mainMode && !fetchingTemplateDetails && (
              <p className="text-sm text-gray-500 mt-2">
                Add one or more main assemblies from the list, then click
                "Done – Select Sub Assemblies".
              </p>
            )}
            <p className="text-red-600 text-sm min-h-5">
              {fieldErrors.main || ""}
            </p>
          </div>
          <div className="bg-white shadow-md rounded-lg mb-5 w-full overflow-hidden">
            <div className="bg-white pb-4">
              <div className="flex flex-wrap items-start md:items-center justify-between py-4 px-6">
                <p className="text-2xl font-semibold mr-2 mb-2 md:mb-0 pr-2">
                  Sub Assemblies
                </p>
                <div className="flex">
                  <ButtonSolid
                    buttonText={"Add Assembly"}
                    onClick={() => setToggleSearchPopup(true)}
                    disabled={fetchingTemplateDetails || mainMode}
                    className="lg:hidden"
                  />
                </div>
              </div>
              {fetchingTemplateDetails ? (
                <LoadingAnimation />
              ) : mains.length === 0 ? (
                <p className="px-6 text-gray-500">
                  Select the main assemblies first to add sub assemblies.
                </p>
              ) : !hasSubAssemblies ? (
                <p className="px-6 text-gray-500">
                  {mainMode
                    ? 'Click "Done – Select Sub Assemblies" to add sub assemblies.'
                    : 'No sub assemblies added yet. Use "+ Add" on the assembly list.'}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  {Object.keys(templateDetails).map(
                    (key, index) =>
                      templateDetails[key].length > 0 && (
                        <div
                          key={key}
                          className={`border border-gray-300 mb-6 mx-6 rounded-lg overflow-hidden min-w-fit  transition-all ease-in-out duration-300 `}
                        >
                          <div className="py-4 px-4 text-xs md:text-base font-semibold bg-slate-50 capitalize text-gray-700 cursor-default">
                            {key}
                          </div>
                          <table
                            key={`${key}-${index}`}
                            id={key}
                            className="w-full min-w-[525px] text-sm text-left rtl:text-right cursor-default"
                          >
                            <thead>
                              <tr className="text-xs md:text-base text-gray-400 uppercase border-b-2 border-dotted h-10">
                                <th className="px-4">{"ASSEMBLY#"}</th>
                                <th className="px-4">FILE</th>
                                <th className="px-4">ASSEMBLY DESCRIPTION</th>
                                <th className="px-4 text-center">ACTION</th>
                              </tr>
                            </thead>
                            {templateDetails[key]?.map((item, itemIndex) => (
                              <tbody key={itemIndex}>
                                <tr
                                  className="hover:bg-slate-200 border bg-transparent text-sm md:text-base text-gray-600"
                                >
                                  <td className="pl-4 py-6 min-w-[200px]">
                                    <div>
                                      <p className="inline-block ml-2">
                                        {item?.assembly_number}
                                      </p>
                                    </div>
                                  </td>
                                  <td className="px-4 py-6 md:min-w-[100px]">
                                    <span
                                      onClick={() => {
                                        showDrawing(item?.assets);
                                      }}
                                      className="cursor-pointer border-b border-gray-600 hover:border-orange-600"
                                    >
                                      Drawing
                                    </span>
                                  </td>
                                  <td className="px-4 py-6 w-full">
                                    {item?.assembly_description}
                                  </td>
                                  <td className="px-4 py-6">
                                    <div
                                      onClick={() => {
                                        deleteSubAssembly(key, item._id);
                                      }}
                                      className="w-fit mx-auto cursor-pointer"
                                    >
                                      <DeleteIcon />
                                    </div>
                                  </td>
                                </tr>
                              </tbody>
                            ))}
                          </table>
                        </div>
                      )
                  )}
                </div>
              )}

              {filesPopup && (
                <PopupWithClose closePopup={setFilesPopup}>
                  <AssetList
                    data={assetsData}
                    fetchingAssets={fetchingAssets}
                  />
                </PopupWithClose>
              )}
            </div>
          </div>
        </div>

        <MODsAssemblyList
          fetchingTemplateDetails={fetchingTemplateDetails}
          addAssemblyData={mainMode ? addMain : addSubAssembly}
          setToggleSearchPopup={setToggleSearchPopup}
          toggleSearchPopup={toggleSearchPopup}
          title={mainMode ? "Select Main Assemblies" : "Select Sub Assemblies"}
          subtitle={
            mainMode
              ? "Add one or more main assemblies for this template, then click Done."
              : undefined
          }
          highlight={mainMode}
          actionLabel={mainMode ? "Add Main" : "+ Add"}
          selectedIds={mainMode ? mainIds : [...mainIds, ...flattenIds(templateDetails)]}
          doneLabel="Done – Select Sub Assemblies"
          onDone={mainMode ? finishMainSelection : undefined}
          doneDisabled={mains.length === 0}
        />

        {openDeletePopup && (
          <DeletePopUp
            onDelete={() => deleteTemplate(templateModelDesc["id"])}
            dataName={`MODSs template ${templateModelDesc["name"]}`}
            setOpenDeletePopup={setOpenDeletePopup}
          />
        )}
      </div>
    </>
  );
}

export default ModsTemplateEdit;
